import React, { useCallback, useContext, useState } from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import { CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { LoadingButton } from '@mui/lab';

import CoachSelect from '../../components/Leads/components/CoachSelect';
import PodContext from '../../../context/PodContext/PodContext';
import FirebaseContext from '../../../context/FirebaseContext';
import { useCoachConfig } from '../../hooks/useCoachConfig';
import CoachPod from '../../../Model/CoachPod';
import useToast from '../../hooks/useToast';
import LoggedInUserContext from '../../../context/LoggedInUserContext';

import CoachHistory, { HistoryEventType } from '../../Model/CoachHistory';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  Container,
  HeaderContainer,
  DetailsContainer,
  HeaderTitle,
  LoadingContainer,
  ButtonContainer,
  HeaderDescription,
} from './styles';

import texts from './texts.json';
import CoachPodTable from './components/CoachPodTable';
import CreateCoachPodModal from './components/CreateCoachPodModal/CreateCoachPodModal';

// eslint-disable-next-line max-len
const notionDocLink = 'https://www.notion.so/system2fitness/How-to-enable-pods-for-coaches-209fe9cbfa708099b099c2f64208963a';

const PodSettings = () => {
  const { fetchPodsForCoach, setAssistantCoachesAndAssignment, pods } = useContext(PodContext);
  const { firebase: { remote } } = useContext(FirebaseContext);
  const {
    userDoc: {
      name: authUserName,
    },
    userId: authUserId,
  } = useContext(LoggedInUserContext);

  const [selectedCoach, setSelectedCoach] = useState({});
  const [loading, setLoading] = useState(false);
  const [isPodCreating, setIsPodCreating] = useState(false);
  const [isPodInitializing, setIsPodInitializing] = useState(false);
  const [isPodReBalancing, setIsPodReBalancing] = useState(false);
  const [createPodModalOpen, setCreatePodModalOpen] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { coachConfig } = useCoachConfig(selectedCoach.id);
  const { showToast } = useToast();

  const handleCoachChange = useCallback(async (coach) => {
    setLoading(true);
    setSelectedCoach(coach || {});

    if (coach?.id) {
      await Promise.all([
        fetchPodsForCoach(coach.id),
        setAssistantCoachesAndAssignment(coach.id),
      ]);
    }

    setLoading(false);
  }, [fetchPodsForCoach, setAssistantCoachesAndAssignment]);

  const handlePodCreate = useCallback(async (name) => {
    try {
      setIsPodCreating(true);
      const podId = await CoachPod.create(selectedCoach.id, name);

      await CoachHistory.addCoachHistoryItem({
        type: HistoryEventType.COACH_POD_CREATED,
        coachId: selectedCoach.id,
        actionedBy: authUserId,
        actionedByUserName: authUserName,
        metadata: {
          podId,
          podName: name,
        },
      });

      showToast(texts.podCreated, { success: true });

      await fetchPodsForCoach(selectedCoach.id);
    } catch (error) {
      showToast(texts.podCreationError, { error: true });
    } finally {
      setIsPodCreating(false);
    }
  }, [authUserId, authUserName, fetchPodsForCoach, selectedCoach.id, showToast]);

  const handlePodInitialize = useCallback(async () => {
    try {
      setIsPodInitializing(true);
      await remote('coachPod-initializePods', { coachId: selectedCoach.id });

      await CoachHistory.addCoachHistoryItem({
        type: HistoryEventType.COACH_POD_INITIALIZED,
        coachId: selectedCoach.id,
        actionedBy: authUserId,
        actionedByUserName: authUserName,
      });

      showToast(texts.podInitialized, { success: true });

      await fetchPodsForCoach(selectedCoach.id);
    } catch (error) {
      showToast(texts.podInitializedError, { error: true });
    } finally {
      setIsPodInitializing(false);
    }
  }, [authUserId, authUserName, fetchPodsForCoach, remote, selectedCoach.id, showToast]);

  const handlePodReBalancing = useCallback(async () => {
    try {
      setShowConfirmationModal(false);
      setIsPodReBalancing(true);
      await remote('coachPod-initializePods/rebalance', { coachId: selectedCoach.id });

      await CoachHistory.addCoachHistoryItem({
        type: HistoryEventType.COACH_POD_REBALANCED,
        coachId: selectedCoach.id,
        actionedBy: authUserId,
        actionedByUserName: authUserName,
      });

      showToast(texts.podReBalanced, { success: true });

      await fetchPodsForCoach(selectedCoach.id);
    } catch (error) {
      showToast(texts.podReBalancedError, { error: true });
    } finally {
      setIsPodReBalancing(false);
    }
  }, [authUserId, authUserName, fetchPodsForCoach, remote, selectedCoach.id, showToast]);

  return (
    <Container>
      <HeaderContainer>
        <HeaderTitle>{texts.title}</HeaderTitle>
        <HeaderDescription>
          {texts.description}
          {' '}
          <a
            href={notionDocLink}
            target="_blank"
            rel="noreferrer"
          >
            {texts.howToUsePods}
          </a>
        </HeaderDescription>
      </HeaderContainer>
      <CoachSelect onChange={handleCoachChange} />
      <DetailsContainer>
        {loading && (
          <LoadingContainer>
            <CircularProgress size={20} color="info" />
          </LoadingContainer>
        )}
        {!loading && !selectedCoach.id && <div>{texts.selectCoach}</div>}
        {!loading && selectedCoach.id && (
          <>
            <ButtonContainer>
              {!coachConfig?.isPodEnabled && (
                <LoadingButton
                  onClick={handlePodInitialize}
                  loading={isPodInitializing}
                  disabled={isPodInitializing}
                  variant="contained"
                >
                  {texts.enablePods}
                </LoadingButton>
              )}
              {coachConfig?.isPodEnabled && (
                <>
                  <LoadingButton
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreatePodModalOpen(true)}
                    loading={isPodCreating}
                    disabled={isPodCreating}
                  >
                    {texts.createPod}
                  </LoadingButton>
                  <LoadingButton
                    variant="contained"
                    onClick={() => setShowConfirmationModal(true)}
                    loading={isPodReBalancing}
                    disabled={isPodReBalancing || pods.length <= 1}
                  >
                    {texts.rebalancePods}
                  </LoadingButton>
                </>
              )}
            </ButtonContainer>
            <CoachPodTable selectedCoach={selectedCoach} />
          </>
        )}
      </DetailsContainer>
      <ConfirmDialog
        isOpen={showConfirmationModal}
        onConfirm={handlePodReBalancing}
        onCancel={() => setShowConfirmationModal(false)}
        dialogTexts={{
          title: texts.confirmationModal.title,
          content: texts.confirmationModal.description,
        }}
      />
      <CreateCoachPodModal
        open={createPodModalOpen}
        setOpen={setCreatePodModalOpen}
        loading={isPodCreating}
        handlePodCreate={handlePodCreate}
      />
    </Container>
  );
};

export default compose(
  observer,
)(PodSettings);
